import type { ActionOut } from "@/lib/organizationService";

/**
 * Turning ledger entries into sentences a person can read.
 *
 * One module rather than inline strings in each view: the actor phrasing is
 * the part that is easy to get subtly wrong — an agent-driven edit rendered as
 * plain "Alice" tells the reader something false, and a scheduled sync
 * attributed to whoever's WordPress credentials it happened to run under is
 * the exact confusion `actor_type` exists to prevent.
 */

/** Resolves a uid to a member's display name — see `orgSlice`. */
type NameResolver = (uid: string) => string;

/**
 * Who did it, in words.
 *
 * - `user`           → "Alice" / "You"
 * - `agent`          → "Assistant, for Alice" — our model, because a member asked
 * - `external_agent` → "External agent, for Alice" — the member's own agent, over MCP
 * - `system`         → "Scheduled" — nobody asked; a cron fired
 *
 * `agent` and `external_agent` are kept apart on purpose: both are machines
 * acting for a member, but one is our assistant inside our UI and the other is
 * a tool the member connected themselves and we do not control. Rendering them
 * the same would tell the reader our assistant did something it never did.
 */
export function describeActor(
  entry: Pick<ActionOut, "actor_uid" | "actor_type" | "on_behalf_of">,
  currentUid: string | null,
  getMemberDisplayName: NameResolver,
): string {
  const name = (uid: string) =>
    uid === currentUid ? "You" : getMemberDisplayName(uid);

  switch (entry.actor_type) {
    case "system":
      return "Scheduled";
    case "agent": {
      // `on_behalf_of` is the authoritative field, but fall back to actor_uid:
      // today core sets both to the same member, and an entry written before
      // `on_behalf_of` existed still knows who asked.
      const requester = entry.on_behalf_of ?? entry.actor_uid;
      return requester ? `Assistant, for ${name(requester)}` : "Assistant";
    }
    case "external_agent": {
      const requester = entry.on_behalf_of ?? entry.actor_uid;
      return requester
        ? `External agent, for ${name(requester)}`
        : "External agent";
    }
    case "user":
      return entry.actor_uid ? name(entry.actor_uid) : "Someone";
  }
}

/**
 * Where they asked for it — a link to the conversation turn behind this entry,
 * or null when there is none to point at.
 *
 * `actor_type` says the assistant did the work and `on_behalf_of` says who for;
 * neither says *why*. Backend-written entries carry the thread and message that
 * triggered them, which is the difference between "Assistant, for Alice
 * generated a persona" and being able to go read the message where Alice asked.
 *
 * The domain rides along because the assistant's threads are subscribed per
 * domain: an entry from another domain has to switch it before the thread
 * resolves.
 *
 * Structurally typed rather than tied to `ActionOut`, because the same two
 * fields now live on the generated documents themselves — a persona, offer or
 * idea card links back the same way a feed row does. It has to: a ledger entry's
 * `target_path` is the page the work happened under, so one `ideas.generated`
 * row covers a whole batch and no single card can find its own entry.
 */
export function conversationLinkFor(entry: {
  domain_id?: string | null;
  source_thread_id?: string | null;
  source_message_id?: string | null;
}): string | null {
  if (!entry.domain_id || !entry.source_thread_id) return null;
  const params = new URLSearchParams({
    domain: entry.domain_id,
    thread: entry.source_thread_id,
  });
  // Optional: an entry can know its conversation without knowing which message.
  if (entry.source_message_id) params.set("message", entry.source_message_id);
  return `/assistant?${params.toString()}`;
}

/**
 * What they did. Keys mirror core's `ActionType`; an unmapped action falls back
 * to its raw name rather than disappearing, so a feed built against an older
 * frontend still shows that *something* happened.
 */
const ACTION_PHRASES: Record<string, string> = {
  "domain.created": "added a domain",
  "domain.deleted": "removed a domain",
  "page.created": "added a page",
  "page.deleted": "removed a page",
  "policy.created": "created a policy",
  "policy.updated": "updated a policy",
  "policy.deleted": "deleted a policy",
  "job.created": "created a scheduled job",
  "job.updated": "changed a scheduled job",
  "job.deleted": "deleted a scheduled job",
  "job.run_started": "started a job run",
  "job.step_ran": "ran a scheduled job step",
  "job.step.recipe_ran": "ran a scheduled assistant workflow",
  "integration.connected": "connected an integration",
  "integration.disconnected": "disconnected an integration",
  "member.added": "added a member",
  "member.removed": "removed a member",
  "member.role_changed": "changed a member's role",
  "member.ownership_transferred": "transferred organization ownership",
  "organization.updated": "updated organization settings",
  "library_document.added": "added a file to the organization library",
  "library_document.deleted": "removed a file from the organization library",
  "external_client.connected": "connected an external application",
  "external_client.disconnected": "disconnected an external application",
  "llm_key.added": "added an AI provider key",
  "llm_key.deleted": "removed an AI provider key",
  "llm_key.activated": "changed which AI provider key the assistant uses",
  "api_key.added": "added a data API key",
  "api_key.updated": "changed a data API key's daily limit",
  "api_key.deleted": "removed a data API key",
  "api_key.called": "called a data API",
  "recipe.invoked": "ran an assistant workflow",
  "heuristic_audit.started": "ran a heuristic audit",
  "ideas.generated": "generated page ideas",
  "persona.generated": "generated a persona",
  "persona.toggled": "changed a persona",
  "offer.generated": "generated an offer",
  "offer.toggled": "changed an offer",
  "insights.generated": "generated insights",
  "wordpress.draft_created": "created a WordPress draft",
  "wordpress.draft_edited": "edited a WordPress draft",
  "wordpress.version_approved": "approved a WordPress version",
  "wordpress.sync_started": "synced WordPress",
};

export function describeAction(action: string): string {
  return ACTION_PHRASES[action] ?? action;
}

/** Short absolute timestamp. Relative times ("2h ago") go stale in a mounted feed. */
export function formatActionTime(at: string): string {
  const date = new Date(at);
  if (Number.isNaN(date.getTime())) return at;
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
